// src/pages/Orders.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Spin,
  Image,
  Input,
  Select,
  Modal,
  Descriptions,
  Tooltip,
  DatePicker,
  Form,
  Input as AntInput,
  Empty,
  Tag
} from "antd";
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  StopOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined as ExclamationCircleOutlinedIcon
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import JsBarcode from "jsbarcode";
import jsPDF from "jspdf";
import { useOrderContext } from "../contexts/OrderContext";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = AntInput;

const splitProductName = (text) => {
  const words = (text || "").split(" ");
  if (words.length <= 2) return text;
  const mid = Math.ceil(words.length / 2);
  return (
    <>
      {words.slice(0, mid).join(" ")}
      <br />
      {words.slice(mid).join(" ")}
    </>
  );
};

const generateBarcodeBase64 = (value) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value || "", {
    format: "CODE128",
    displayValue: false,
    width: 3,
    height: 50,
    margin: 10,
    background: "#ffffff",
    lineColor: "#000000",
  });
  return canvas.toDataURL("image/png");
};

const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

/** ✅ NEW: Special Quote parser (text + files) */
const safeJsonParse = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const resolveUrl = (u) => {
  const url = String(u || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

  const base = (axiosInstance?.defaults?.baseURL || "").trim();
  if (!base) return url;

  const b = base.replace(/\/$/, "");
  const p = url.startsWith("/") ? url : `/${url}`;
  return `${b}${p}`;
};

const fileNameFromUrl = (url) => {
  try {
    const clean = String(url || "").split("?")[0].split("#")[0];
    const parts = clean.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "file");
  } catch {
    return "file";
  }
};

const isPdfUrl = (url) => /\.pdf(\?|#|$)/i.test(String(url || ""));
const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(String(url || ""));

const normalizeSpecialQuote = (order) => {
  const raw =
    order?.special_quote ||
    order?.specialQuote ||
    order?.specialInstruction ||
    order?.specialInstructions ||
    order?.special_quote_json ||
    null;

  const obj = safeJsonParse(raw);

  // Sometimes backend might send just text directly
  const text =
    (obj && (obj.text || obj.note || obj.message)) ||
    (typeof raw === "string" && !obj ? raw : "") ||
    "";

  const filesRaw =
    (obj && (obj.files || obj.attachments || obj.uploads)) ||
    order?.files ||
    order?.attachments ||
    [];

  const filesArr = Array.isArray(filesRaw) ? filesRaw : [];

  const files = filesArr
    .map((f) => {
      if (!f) return null;

      // string -> url
      if (typeof f === "string") {
        const url = resolveUrl(f);
        const name = fileNameFromUrl(url);
        return {
          url,
          name,
          isPdf: isPdfUrl(url),
          isImage: isImageUrl(url),
        };
      }

      // object -> try common keys
      const url =
        resolveUrl(
          f.url ||
            f.fileUrl ||
            f.path ||
            f.file ||
            f.location ||
            f.link ||
            f.src ||
            ""
        ) || "";

      if (!url) return null;

      const name =
        f.name ||
        f.fileName ||
        f.filename ||
        f.originalName ||
        f.original_name ||
        fileNameFromUrl(url);

      const mime = String(f.mimeType || f.mimetype || f.type || "").toLowerCase();
      const isPdf = mime.includes("pdf") || isPdfUrl(url);
      const isImage = mime.startsWith("image/") || isImageUrl(url);

      return { url, name, isPdf, isImage };
    })
    .filter(Boolean);

  const hasAny = Boolean(String(text || "").trim()) || files.length > 0;

  return { text: String(text || ""), files, hasAny };
};

const Orders = () => {
  const {
    orders,
    loading,
    pagination,
    setPagination,
    deliveryStatuses,
    selectedRows,
    selectedRowKeys,
    setSelectedRows,
    setSelectedRowKeys,
    userRole,
    isSales,
    isOrderCanceller,
    fetchOrders,
    updateOrderStatus,
    updateOrderPriority,
    updateDeliveryStatus,
    cancelOrderWithReason,
    deleteOrder,
  } = useOrderContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("");
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelForm] = Form.useForm();
  const [customerFilter, setCustomerFilter] = useState(null);

  // NEW: category filter state
  const [categoryFilter, setCategoryFilter] = useState(null);

  // NEW: incoming scope from Home (today / month / awaiting_delivery)
  const location = useLocation();
  const [incomingScope, setIncomingScope] = useState(null);

  useEffect(() => {
    if (location.state?.ordersScope) {
      setIncomingScope(location.state.ordersScope);
      // clear state so navigating back doesn't keep reapplying unexpectedly
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // NEW: stock filter (null = all, 'in' = In/Low stock, 'out' = Out of stock)
  const [stockFilter, setStockFilter] = useState(null);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.state?.updatedProduct) {
      fetchOrders();
      navigate("/orders", { replace: true });
    }
  }, [location.state, navigate, fetchOrders]);

  const generateLabelPDF = (record) => {
    const labelX = 40;
    const labelY = 40;
    const labelWidth = 300;
    const labelHeight = 280;
    const pageWidth = labelWidth + 2 * labelX;
    const pageHeight = labelHeight + 2 * labelY;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: [pageWidth, pageHeight],
      compress: true
    });

    doc.setProperties({
      title: `Shipping Label - ${record.trackingId || ""}`,
      subject: "Shipping Label",
      author: "Your Company Name",
      keywords: "shipping, label, tracking",
      creator: "Your App Name"
    });

    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(1.5);
    doc.roundedRect(labelX, labelY, labelWidth, labelHeight, 5, 5, "S");

    doc.setFillColor(245, 245, 245);
    doc.roundedRect(labelX + 1, labelY + 1, labelWidth - 2, labelHeight - 2, 4, 4, "F");

    const sectionHeights = [40, 70, 40, 40, 60];
    let currentY = labelY;
    for (let i = 0; i < sectionHeights.length - 1; i++) {
      currentY += sectionHeights[i];
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(labelX + 10, currentY, labelX + labelWidth - 10, currentY);
    }

    const centerX = labelX + labelWidth / 2;

    const drawLabelText = (text, sectionIndex, options = {}) => {
      const { fontSize = 12, fontStyle = "normal", isHeader = false } = options;
      doc.setFont("helvetica", fontStyle);
      doc.setFontSize(fontSize);

      const sectionTop = labelY + sectionHeights.slice(0, sectionIndex).reduce((a, b) => a + b, 0);
      const sectionHeight = sectionHeights[sectionIndex];

      doc.setTextColor(isHeader ? 60 : 20, isHeader ? 60 : 20, isHeader ? 60 : 20);

      if (sectionIndex === 1) {
        const maxWidth = labelWidth - 30;
        const lines = doc.splitTextToSize(text || "", maxWidth);
        const lineHeight = fontSize * 1.2;
        const totalTextHeight = lines.length * lineHeight;
        let startY = sectionTop + (sectionHeight - totalTextHeight) / 2 + lineHeight / 2;

        lines.forEach((line, i) => {
          const useFontSize = i === 0 ? fontSize + 2 : fontSize;
          doc.setFontSize(useFontSize);
          doc.text(line, centerX, startY, { align: "center" });
          startY += lineHeight;
        });
      } else {
        const sectionCenterY = sectionTop + sectionHeight / 2 - 2;
        doc.text(text || "", centerX, sectionCenterY, { align: "center" });
      }
    };

    const safeDate = (d) => {
      if (!d) return "";
      const maybeISO = new Date(d);
      if (!isNaN(maybeISO)) return maybeISO.toLocaleDateString();
      return String(d);
    };
    const orderDate = safeDate(record.orderDate);

    drawLabelText(`TRACKING #: ${record.trackingId || ""}`, 0, {
      fontSize: 14,
      fontStyle: "bold",
      isHeader: true
    });

    drawLabelText(record.product_name || "", 1, {
      fontSize: 16,
      fontStyle: "bold",
      isHeader: false
    });

    drawLabelText(`Order Date: ${orderDate}`, 2, {
      fontSize: 12,
      isHeader: false
    });

    drawLabelText(`Ref: ${record.referenceNumber || ""}`, 3, {
      fontSize: 12,
      isHeader: false
    });

    const barcodeValue = record.BarcodeData ? String(record.BarcodeData) : "123456";
    const barcodeImg = generateBarcodeBase64(barcodeValue);

    const barcodeSectionTop = labelY + sectionHeights.slice(0, 4).reduce((a, b) => a + b, 0);
    const barcodeHeight = 50;
    const barcodeWidth = 200;
    const barcodeY = barcodeSectionTop + (sectionHeights[4] - barcodeHeight) / 2;
    const barcodeX = centerX - barcodeWidth / 2;

    doc.setFillColor(255, 255, 255);
    doc.rect(barcodeX - 5, barcodeY - 5, barcodeWidth + 10, barcodeHeight + 10, "F");
    doc.addImage(barcodeImg, "PNG", barcodeX, barcodeY, barcodeWidth, barcodeHeight);

    const cleanProductName = (record.product_name || "label").replace(/[^a-z0-9]/gi, "_").substring(0, 20);
    doc.save(`Label_${record.trackingId || "0000"}_${cleanProductName}.pdf`);
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleCancelClick = (rec) => {
  if (rec.status === "cancelled") return;

  // ✅ SALES CANNOT CANCEL
  if (isSales) {
    message.error("Sales users can only view orders.");
    return;
  }

  // ✅ Only OrderCanceller gets reason modal
  if (isOrderCanceller) {
    setOrderToCancel(rec);
    setCancelModalVisible(true);
  } else {
    updateOrderStatus(rec.id, "cancelled");
  }
};

  const handleDeliveryChange = async (val, rec) => {
    await updateDeliveryStatus(rec.id, val);

    const v = String(val || "").toLowerCase();
    const isDelivered = v === "order delivered" || v === "delivered" || v === "order-delivered";
    if (isDelivered && rec.status !== "cancelled") {
      updateOrderStatus(rec.id, "completed");
    }
  };

  const handlePriorityChange = (val, rec) => updateOrderPriority(rec.id, val);

  const handleProductionChange = async (val, rec) => {
    try {
      await axiosInstance.put(`/order/sheet-production-status/${rec.id}`, {
        sheetProductionStatus: val,
      });
      message.success("Production status updated");
      fetchOrders();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to update production status");
    }
  };

  const handleUndoCancel = (rec) => {
    confirm({
      title: `Undo cancellation for order #${rec.id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This will restore the order back to processing.",
      okText: "Yes, Undo",
      cancelText: "No",
      onOk: async () => {
        try {
          await updateOrderStatus(rec.id, "processing");
          message.success("Order restored to processing.");
          if (typeof fetchOrders === "function") fetchOrders();
        } catch (err) {
          console.error("Undo cancel failed:", err);
          message.error("Failed to undo cancellation.");
        }
      },
    });
  };


  const handleCancelOrder = async () => {
    const success = await cancelOrderWithReason(orderToCancel.id, cancelReason);
    if (success) {
      setCancelModalVisible(false);
      setCancelReason("");
      setOrderToCancel(null);
      cancelForm.resetFields();
    }
  };

  const handleDelete = (rec) =>
    confirm({
      title: `Delete order #${rec.id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This cannot be undone.",
      onOk: async () => {
        const success = await deleteOrder(rec.id);
        if (success) {
          const isLastItemOnPage = orders.length === 1;
          const isNotFirstPage = pagination.current > 1;
          const newCurrent = isLastItemOnPage && isNotFirstPage
            ? pagination.current - 1
            : pagination.current;
          setPagination({ ...pagination, current: newCurrent });
        }
      },
    });

  const handleDeleteSelected = () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one order.");
      return;
    }
    confirm({
      title: "Are you sure you want to delete the selected orders?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      onOk: async () => {
        await Promise.all(selectedRowKeys.map(id => deleteOrder(id)));
        setSelectedRowKeys([]);
      },
    });
  };

  const handleSendToProduction = async () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one order.");
      return;
    }
    try {
      await axiosInstance.post("/admin/production/send", { orderIds: selectedRowKeys });
      message.success("Selected orders sent to production.");
      setSelectedRowKeys([]);
      fetchOrders();
      navigate("/production-orders");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to send to production.");
    }
  };

  const handleView = (rec) => {
    setSelectedOrder(rec);
    setViewModalVisible(true);
  };

  const closeView = () => {
    setViewModalVisible(false);
    setSelectedOrder(null);
  };

  // Stock status helper function
  const getStockStatus = (order) => {
    const stock = order.product?.stock;
    const quantity = order.quantity || 0;

    if (stock === null || stock === undefined) {
      return { status: 'unknown', text: 'Unknown', color: 'default' };
    }

    if (stock >= quantity) {
      return { status: 'in_stock', text: 'In Stock', color: 'success' };
    } else if (stock > 0) {
      return { status: 'low_stock', text: `Low Stock (${stock})`, color: 'warning' };
    } else {
      return { status: 'out_of_stock', text: 'Out of Stock', color: 'error' };
    }
  };

  const customerOptions = useMemo(() => {
    const counts = new Map();
    (orders || []).forEach(o => {
      const name = getCompanyName(o) || "-";
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [orders]);

  // NEW: compute category options from orders (uses productCategoryDisplay returned by backend)
  const categoryOptions = useMemo(() => {
    const counts = new Map();
    (orders || []).forEach(o => {
      const cat = (o.productCategoryDisplay || "Other") || "Other";
      counts.set(cat, (counts.get(cat) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [orders]);

  // Start with all orders
  let working = orders || [];

  // NEW: apply incoming scope from Home navigation
  if (incomingScope === "today") {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    working = working.filter(o => {
      const dt = new Date(o.orderDate);
      return !isNaN(dt) && dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    });
  } else if (incomingScope === "month") {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth();
    working = working.filter(o => {
      const dt = new Date(o.orderDate);
      return !isNaN(dt) && dt.getFullYear() === y && dt.getMonth() === m;
    });
  } else if (incomingScope === "awaiting_delivery") {
    working = working.filter(o =>
      String(o.deliveryStatus || "").toLowerCase().includes("awaiting")
    );
  }

  if (customerFilter) {
    working = working.filter(o => (getCompanyName(o) || "-") === customerFilter);
  }

  // NEW: apply category filter
  if (categoryFilter) {
    working = working.filter(o => (o.productCategoryDisplay || "Other") === categoryFilter);
  }

  // NEW: apply stock filter
  if (stockFilter === 'in') {
    working = working.filter(o => {
      const s = getStockStatus(o).status;
      return s === 'in_stock' || s === 'low_stock';
    });
  } else if (stockFilter === 'out') {
    working = working.filter(o => getStockStatus(o).status === 'out_of_stock');
  }

  const filtered = working.filter((o) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const company = (getCompanyName(o) || "").toLowerCase();
    return (
      String(o.id).includes(s) ||
      (o.product_name || "").toLowerCase().includes(s) ||
      (o.referenceNumber || "").toLowerCase().includes(s) ||
      (o.deliveryStatus || "").toLowerCase().includes(s) ||
      company.includes(s)
    );
  });

  const toDate = (d) => {
    if (!d) return 0;
    const t = new Date(d).getTime();
    return isNaN(t) ? 0 : t;
  };

  const sorted = [...filtered];
  if (sortType === "date-asc") {
    sorted.sort((a, b) => toDate(a.orderDate) - toDate(b.orderDate));
  } else if (sortType === "date-desc") {
    sorted.sort((a, b) => toDate(b.orderDate) - toDate(a.orderDate));
  }

  const columns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 70,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </div>
      )
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, rec) => {
        const companyName = getCompanyName(rec);

        const getInitials = (name) => {
          if (!name || name === "-") return "C";
          return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
        };

        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {getInitials(companyName)}
            </div>
            <div className="text-sm font-medium text-gray-900 leading-tight min-w-0 flex-1">
              {companyName}
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 110,
      render: (d) => {
        const date = new Date(d);
        const val = isNaN(date)
          ? String(d || "-")
          : date.toLocaleDateString(undefined, { year: "2-digit", month: "2-digit", day: "2-digit" });

        return (
          <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
            {val}
          </div>
        );
      },
    },
    {
      title: "Size",
      key: "size",
      width: 100,
      render: (_, rec) => {
        const val = rec.size || "-";
        return (
          <div
            className="text-xs font-medium text-gray-800 bg-gray-50 border border-gray-200 px-2 py-1 rounded text-center truncate max-w-[8rem]"
            title={String(val)}
          >
            {String(val)}
          </div>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      width: 120,
      render: (text) => (
        <div className="truncate max-w-[14rem]" title={String(text || "")}>
          {splitProductName(text)}
        </div>
      ),
    },
    {
      title: "Fabric / Colour",
      key: "fabric_colour",
      width: 160,
      render: (_, rec) => {
        const fabric = rec.fabric_name || "-";
        const color  = rec.fabric_color_name || "-";

        return (
          <div className="flex flex-col text-center leading-tight">
            <span className="text-xs font-semibold text-gray-900 truncate" title={fabric}>
              {fabric}
            </span>
            <span className="text-[11px] text-gray-500 truncate" title={color}>
              {color}
            </span>
          </div>
        );
      },
    },
    {
      title: "Customer Ref",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 150,
      render: (val) => {
        const text = String(val || "-");

        const handleCopy = (e) => {
          if (e?.clipboardData) {
            e.clipboardData.setData("text/plain", text);
            e.preventDefault();
          }
        };

        return (
          <div
            className="font-mono text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap"
            title={text}
            onCopy={handleCopy}
          >
            <span className="block max-w-[10ch] truncate">
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (val) => (
        <div className="text-center font-bold text-gray-700">
          {val}
        </div>
      )
    },
    {
      title: "Stock",
      key: "stock_status",
      width: 130,
      render: (_, rec) => {
        const stockInfo = getStockStatus(rec);
        const stockQuantity = rec.product?.stock ?? 'Unknown';

        return (
          <Tooltip title={`Available Stock: ${stockQuantity}`}>
            <Tag
              icon={(
                stockInfo.status === 'in_stock' ? <CheckCircleOutlined /> :
                stockInfo.status === 'low_stock' ? <ExclamationCircleOutlinedIcon /> :
                stockInfo.status === 'out_of_stock' ? <CloseCircleOutlined /> : null
              )}
              color={(
                stockInfo.status === 'in_stock' ? 'success' :
                stockInfo.status === 'low_stock' ? 'warning' :
                stockInfo.status === 'out_of_stock' ? 'error' : 'default'
              )}
              className="text-xs font-medium"
            >
              {stockInfo.status === 'low_stock' ? `${stockInfo.text} (${stockQuantity})` : stockInfo.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Priority",
      key: "priority",
      width: 100,
      render: (_, rec) =>
        isSales || isOrderCanceller ? (
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
            rec.priority === "urgent"
              ? "bg-red-100 text-red-800 border border-red-200"
              : "bg-green-100 text-green-800 border border-green-200"
          }`}>
            {rec.priority}
          </span>
        ) : (
          <Select
            value={rec.priority}
            style={{ width: 80 }}
            onChange={(v) => updateOrderPriority(rec.id, v)}
            className="priority-select"
          >
            <Option value="normal">Normal</Option>
            <Option value="urgent">Urgent</Option>
          </Select>
        ),
    },
    {
      title: "Schedule Date",
      dataIndex: "scheduleDate",
      key: "scheduleDate",
      width: 90,
      render: (d) => {
        const date = new Date(d);
        const text = d ? (isNaN(date) ? String(d) : date.toLocaleDateString()) : "-";
        return (
          <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded text-center font-medium">
            {text}
          </div>
        );
      },
    },
    {
      title: "Delivery Status",
      key: "delivery",
      width: 160,
      render: (_, rec) => {
        const raw = String(rec.deliveryStatus || "").toLowerCase();

        let config = {
          color: "default",
          icon: null,
          text: rec.deliveryStatus || "Not set",
          tooltip: rec.deliveryStatus || "No delivery status set",
        };

        if (raw.includes("awaiting")) {
          config = {
            color: "orange",
            icon: <ExclamationCircleOutlinedIcon />,
            text: rec.deliveryStatus,
            tooltip: "Order is awaiting dispatch",
          };
        } else if (raw.includes("scheduled")) {
          config = {
            color: "purple",
            icon: <ClockCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Delivery has been scheduled",
          };
        } else if (raw.includes("delivered")) {
          config = {
            color: "green",
            icon: <CheckCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Order delivered to customer",
          };
        } else if (raw.includes("cancel")) {
          config = {
            color: "red",
            icon: <CloseCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Delivery has been cancelled",
          };
        }

        return (
          <Tooltip title={config.tooltip}>
            <Tag
              icon={config.icon}
              color={config.color}
              className="text-xs font-medium px-3 py-1 rounded-full"
            >
              {config.text}
            </Tag>
          </Tooltip>
        );
      },
    },
   {
  title: "Actions",
  key: "actions",
  width: 150,
  fixed: "right",
  render: (_, rec) => (
    <Space size="small">
      {/* VIEW always */}
      <Tooltip title="View Details">
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleView(rec)}
          className="bg-blue-500 hover:bg-blue-600 border-blue-500"
          size="small"
        />
      </Tooltip>

      {/* ✅ Cancel/Undo ONLY if NOT sales */}
      {!isSales && (
        <>
          {rec.status === "cancelled" ? (
            <Tooltip title="Undo cancellation">
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => handleUndoCancel(rec)}
                size="small"
                className="bg-green-100 text-green-700 border border-green-300 hover:bg-green-200"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Cancel order">
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancelClick(rec)}
                size="small"
                className="hover:bg-red-50"
              />
            </Tooltip>
          )}
        </>
      )}

      {/* Delete already hidden for sales in your code */}
      {!(isSales || isOrderCanceller) && (
        <Tooltip title="Delete">
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(rec)} size="small" />
        </Tooltip>
      )}
    </Space>
  ),
}
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Orders Management</h1>
                <p className="text-gray-600">Manage and track all customer orders</p>
              </div>

              {!(isSales || isOrderCanceller) && (
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="primary"
                    onClick={handleSendToProduction}
                    className="bg-green-500 hover:bg-green-600 border-green-500 h-10 px-4"
                  >
                    Send to Production
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={handleDeleteSelected}
                    className="h-10 px-4"
                  >
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Orders
                </label>
                <Input
                  placeholder="Search by ID, product, reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Sort By
                </label>
                <Select
                  placeholder="Sort orders..."
                  value={sortType || undefined}
                  onChange={setSortType}
                  allowClear
                  className="w-full h-10 rounded-lg"
                >
                  <Option value="date-asc">Date ↑ (Oldest First)</Option>
                  <Option value="date-desc">Date ↓ (Newest First)</Option>
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <UserOutlined className="mr-2" />
                  Filter by Customer
                </label>
                <Select
                  placeholder="Select customer..."
                  value={customerFilter || undefined}
                  onChange={setCustomerFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                  optionFilterProp="label"
                  showSearch
                >
                  {customerOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Category
                </label>
                <Select
                  placeholder="Select category..."
                  value={categoryFilter || undefined}
                  onChange={setCategoryFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                  optionFilterProp="label"
                  showSearch
                >
                  {categoryOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Stock Filter
                </label>
                <Select
                  placeholder="All stock statuses"
                  value={stockFilter || undefined}
                  onChange={setStockFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                >
                  <Option value="in">In Stock (incl. Low)</Option>
                  <Option value="out">Out of Stock</Option>
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Order Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {sorted.length} orders
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={sorted}
              columns={columns}
              rowKey="id"
              rowSelection={
                !(isSales || isOrderCanceller)
                  ? {
                      selectedRowKeys,
                      onChange: (keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedRows(rows);
                      },
                    }
                  : undefined
              }
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              className="modern-orders-table"
              rowClassName={(record) => {
                const baseClasses = "hover:bg-gray-50 transition-colors duration-200";
                if (record.status === "cancelled") {
                  return `${baseClasses} bg-red-50 border-l-4 border-l-red-400`;
                }
                if ((record.priority || "").toLowerCase() === "urgent") {
                  return `${baseClasses} bg-orange-50 border-l-4 border-l-orange-400`;
                }
                return baseClasses;
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="text-gray-500">
                        <div className="text-lg font-medium mb-2">No orders found</div>
                        <div className="text-sm">Try adjusting your search or filters</div>
                      </div>
                    }
                  />
                )
              }}
            />
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <StopOutlined className="text-red-500" />
            <span>Cancel Order #{orderToCancel?.id || ""}</span>
          </div>
        }
        open={cancelModalVisible}
        onOk={handleCancelOrder}
        onCancel={() => {
          setCancelModalVisible(false);
          setCancelReason("");
          setOrderToCancel(null);
          cancelForm.resetFields();
        }}
        okText="Confirm Cancellation"
        cancelText="Keep Order"
        okButtonProps={{ danger: true }}
        className="modern-modal"
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            label="Reason for Cancellation"
            name="cancelReason"
            rules={[{ required: true, message: 'Please provide a cancellation reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter the reason for cancelling this order..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="rounded-lg border-gray-300"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Order Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <EyeOutlined className="text-blue-500" />
            <span>Order Details #{selectedOrder?.id || ""}</span>
          </div>
        }
        open={viewModalVisible}
        onCancel={closeView}
        footer={[
          <Button key="close" onClick={closeView} className="rounded-lg">
            Close
          </Button>
        ]}
        width={600}
        className="modern-modal"
      >
        {selectedOrder && (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Customer</div>
                <div className="font-semibold text-gray-900">{getCompanyName(selectedOrder)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Product Name</div>
                <div className="font-semibold text-gray-900">{selectedOrder.product_name}</div>
              </div>
            </div>

            {/* Product Image */}
            {selectedOrder.product_image && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">Product Image</div>
                <Image
                  src={selectedOrder.product_image}
                  width={200}
                  height={140}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                  preview={{ src: selectedOrder.product_image }}
                  className="border border-gray-200"
                />
              </div>
            )}

            {/* Order Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Bed Size</div>
                  <div className="font-semibold text-gray-900">{selectedOrder.size || "—"}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Fabric Type</div>
                  <div className="font-semibold text-gray-900">{selectedOrder.fabric_name || "—"}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Fabric Colour</div>
                  <div className="font-semibold text-gray-900">{selectedOrder.fabric_color_name || "—"}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Storage Type</div>
                  <div className="font-semibold text-gray-900">
                    {selectedOrder.storageType || "Non Storage"}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Quantity</div>
                  <div className="font-semibold text-gray-900">{selectedOrder.quantity}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Customer Reference</div>
                  <div className="font-semibold text-gray-900">{selectedOrder.referenceNumber || "-"}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Priority</div>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                      selectedOrder.priority === "urgent"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {selectedOrder.priority}
                  </span>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Order Status</div>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                      selectedOrder.status === "cancelled"
                        ? "bg-red-100 text-red-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {selectedOrder.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Stock Status in View Modal */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Stock Status</div>
              <div className="font-medium">
                {(() => {
                  const stockInfo = getStockStatus(selectedOrder);
                  return (
                    <Tag
                      icon={
                        stockInfo.status === 'in_stock' ? <CheckCircleOutlined /> :
                        stockInfo.status === 'low_stock' ? <ExclamationCircleOutlinedIcon /> :
                        stockInfo.status === 'out_of_stock' ? <CloseCircleOutlined /> : null
                      }
                      color={
                        stockInfo.status === 'in_stock' ? 'success' :
                        stockInfo.status === 'low_stock' ? 'warning' :
                        stockInfo.status === 'out_of_stock' ? 'error' : 'default'
                      }
                    >
                      {stockInfo.text} {stockInfo.status === 'low_stock' && `(Available: ${selectedOrder.product?.stock || 0})`}
                    </Tag>
                  );
                })()}
              </div>
            </div>

            {/* ✅ NEW: Special Instructions (text + attachments) */}
            {(() => {
              const special = normalizeSpecialQuote(selectedOrder);
              if (!special.hasAny) return null;

              const pdfs = special.files.filter(f => f.isPdf);
              const nonPdfs = special.files.filter(f => !f.isPdf);

              return (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-2">Special Instructions</div>

                  {String(special.text || "").trim() && (
                    <div className="font-medium text-gray-900 whitespace-pre-wrap">
                      {special.text}
                    </div>
                  )}

                  {(special.files || []).length > 0 && (
                    <div className="mt-3 space-y-3">
                      {/* Images / other files */}
                      {nonPdfs.length > 0 && (
                        <div className="space-y-2">
                          {nonPdfs.map((f, idx) => (
                            <div key={`${f.url}-${idx}`} className="flex items-center gap-3">
                              {f.isImage ? (
                                <Image
                                  src={f.url}
                                  width={80}
                                  height={60}
                                  style={{ objectFit: "cover", borderRadius: 8 }}
                                  preview={{ src: f.url }}
                                  className="border border-gray-200"
                                />
                              ) : (
                                <div className="text-sm text-gray-700">
                                  <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                                    {f.name || "Open file"}
                                  </a>
                                </div>
                              )}
                              {!f.isImage && (
                                <div className="text-xs text-gray-500 truncate" title={f.url}>
                                  {f.url}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* PDFs (LINK ONLY) */}
{pdfs.length > 0 && (
  <div className="space-y-2">
    {pdfs.map((f, idx) => (
      <div key={`${f.url}-${idx}`} className="text-sm">
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 underline"
        >
          {f.name || "Open PDF"}
        </a>
      </div>
    ))}
  </div>
)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ✅ FIX: Cancellation Reason ONLY show when order is cancelled */}
            {String(selectedOrder.status || "").toLowerCase() === "cancelled" && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">Cancellation Reason</div>
                <div className="font-medium text-gray-900">
                  {selectedOrder.cancelReason ||
                    selectedOrder.cancel_reason ||
                    selectedOrder.cancellationReason ||
                    "No cancellation reason provided"}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc; color: #374151; font-weight: 600;
          border-bottom: 2px solid #e5e7eb; white-space: nowrap; padding: 12px 16px;
        }
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9; padding: 12px 16px; transition: all 0.2s ease;
        }
        .modern-modal .ant-modal-content { border-radius: 16px; overflow: hidden; }
        .modern-modal .ant-modal-header { border-bottom: 1px solid #e5e7eb; background: #f8fafc; }
        .priority-select .ant-select-selector,
        .delivery-status-select .ant-select-selector { border-radius: 8px !important; }
      `}</style>
    </Spin>
  );
};

export default Orders;